<template>
  <div>
    <ProductFilters
      :endpoint="endpoint"
      :searchPlaceholder="searchPlaceholder"
      :loading="isLoading"
      :allProducts="allProducts"
      ref="productFilters"
      @filters-changed="handleFiltersChanged"
    />
    <div
      class="product-grid-container"
      :class="{
        'product-grid-container--loading':
          blurLoader && this.allProducts.length > 0,
      }"
    >
      <!-- Loading state - toon skeleton tijdens laden -->
      <div
        class="row g-2 mt-2"
        v-if="isLoading"
        :class="{ 'fade-out': !isLoading }"
      >
        <div class="col-6 col-md-4" v-for="n in localSearch.length ? 6 : 18">
          <div class="skeleton skeleton--product-card"></div>
        </div>
      </div>

      <!-- Producten grid - toon alleen wanneer niet loading en er producten zijn -->
      <div
        class="row g-2 mt-2"
        v-else-if="!isLoading && allProducts.length > 0"
        :class="{ 'fade-in': !isLoading && allProducts.length > 0 }"
      >
        <template
          v-for="(product, index) in allProducts"
          :key="`product-${product.id}`"
        >
          <div class="col-6 col-md-4">
            <Card
              :selectedAsPackage="
                product.id === this.data?.packagingProduct?.id
              "
              :product="product"
              :count="quantityOf(product.id)"
              :isPackaging="isPackaging"
              :isPersonalizedCard="false"
              :packageType="packageType"
              :selected="isSelected(product)"
              :isImageClickable="true"
              @select="selectProduct(product)"
              @increase="increase(product)"
              @decrease="decrease(product)"
              @remove="remove(product)"
              @open-modal="openProductDetailPopup(product)"
            />
          </div>

          <!-- Mobile CTA - show halfway through products, after a complete row (2 items per row on mobile) -->
          <div v-if="index === mobileCTAIndex" class="col-12 d-xl-none my-2">
            <SidebarCta :flowType="flowType" :currentStep="currentStep" />
          </div>
        </template>
      </div>

      <!-- Empty state - toon alleen wanneer niet loading, heeft gezocht, en geen resultaten -->
      <div
        class="row g-2 mt-2"
        v-else-if="
          !isLoading &&
          hasSearched &&
          allProducts.length === 0 &&
          (localSearch || hasActiveFilters)
        "
        :class="{ 'fade-in': !isLoading && allProducts.length === 0 }"
      >
        <div class="col-12 text-center mt-5">
          <div class="d-flex flex-column p-5">
            <span class="d-block h3 mb-3"
              >Geen producten gevonden voor je pakket</span
            >
            <div class="d-block fs-sm text-gray-700 mb-4">
              <div v-if="localSearch" class="mb-2">
                <strong>"{{ localSearch }}"</strong> leverde geen resultaten
                op{{
                  hasActiveFilters
                    ? ' in ' + getActiveFilterCategoriesText()
                    : ''
                }}.
              </div>
              <div v-if="hasActiveFilters && !localSearch" class="mb-2">
                Geen producten gevonden in
                <span class="text-black">{{
                  getActiveFilterCategoriesText()
                }}</span
                >.
              </div>
            </div>
            <div class="d-flex flex-wrap justify-content-center gap-3 mb-4">
              <a
                href="#"
                class="btn btn--gray"
                @click.prevent="clearSearch"
                v-if="localSearch"
              >
                {{
                  isMobileDevice()
                    ? 'Zoekterm verwijderen'
                    : 'Zoekterm aanpassen'
                }}<i
                  :class="
                    isMobileDevice()
                      ? 'icon icon-close ms-2'
                      : 'icon icon-edit ms-2'
                  "
                ></i>
              </a>

              <a
                href="#"
                class="btn btn--gray"
                @click.prevent="resetAllFilters"
                v-if="hasActiveFilters"
              >
                Filters resetten<i class="icon icon-refresh ms-2"></i>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Paginatie -->
    <Pagination
      v-if="!isLoading && showPagination"
      :currentPage="currentPage"
      :totalPages="paginatedData.totalPages"
      :loading="isChangingPage"
      @page-changed="goToPage"
    />
  </div>

  <!-- Detail modal -->
  <ProductDetailModal
    v-if="showProductDetailModal"
    :product="productDetailModalProduct"
    :count="quantityOf(productDetailModalProduct.id)"
    :isPackaging="isPackaging"
    :isPersonalizedCard="false"
    :packageType="packageType"
    :selected="isSelected(productDetailModalProduct)"
    @select="selectProduct(productDetailModalProduct)"
    @decrease="decrease(productDetailModalProduct)"
    @increase="increase(productDetailModalProduct)"
    @remove="remove(productDetailModalProduct)"
    @close-modal="showProductDetailModal = false"
  />
</template>

<script>
import Card from './Card.vue';
import ProductFilters from '../components/ProductFilters.vue';
import Pagination from '../components/Pagination.vue';
import {
  getProductCount,
  increaseProductCount,
  decreaseProductCount,
  removeProduct as removeProductUtil,
  removePersonalization as removePersonalizationUtil,
} from '../utils/productCountUtils.js';
import SidebarCta from '../Sidebar/SidebarCta.vue';
import ProductDetailModal from '../modals/ProductDetailModal.vue';
import {
  debounce,
  simpleDebounce,
  RequestDeduplicator,
} from '../utils/performanceUtils.js';
import {
  deviceUtils,
  htmlUtils,
  validationUtils,
  errorUtils,
} from '../utils/componentUtils.js';
import { handleVersionChange } from '../utils/cacheUtils.js';
import { usePagination } from '../composables/usePagination.js';

window.globalProductCache = {};

export default {
  setup() {
    // Use pagination composable for clean, reusable pagination logic
    const {
      currentPage,
      isChangingPage,
      paginate,
      goToPage: paginationGoToPage,
      resetPagination,
    } = usePagination({
      perPage: 72,
      scrollTarget: '.product-grid-container',
    });

    return {
      // Pagination state
      currentPage,
      isChangingPage,
      paginate,
      paginationGoToPage,
      resetPagination,
    };
  },
  props: {
    endpoint: { type: String, required: true },
    data: { type: Object, required: true },
    errors: { type: Object, default: () => ({}) },
    isPackaging: { type: Boolean, default: false },
    dataKey: { type: String, default: 'products' },
    packageType: { type: String, default: 'large' },
    mode: { type: String, default: null },
    isPackage: { type: Boolean, default: null },
    isSingleSelection: { type: Boolean, default: null },
    selectedItems: { type: Array, default: () => [] },
    isMailboxSession: { type: Boolean, default: false },
    flowType: { type: String, default: 'compose' },
    currentStep: { type: Number, default: 0 },
  },
  components: {
    ProductDetailModal,
    SidebarCta,
    Card,
    ProductFilters,
    Pagination,
  },
  emits: [
    'update',
    'next',
    'goToStep',
    'resetPackage',
    'start',
    'increaseProduct',
    'decreaseProduct',
    'addToCart',
    'removeProduct',
    'removeFood',
    'removePackaging',
    'selectBase',
    'printingUpdated',
    'removeBasePackage',
  ],
  data() {
    return {
      allProducts: [],
      localSearch: '',
      blurLoader: false,
      isLoading: true,
      showProductDetailModal: false,
      productDetailModalProduct: {},
      debounceTimeout: null,
      fetchController: null,
      requestId: 0,
      hasSearched: false,
      currentFilters: {
        search: '',
        filters: {
          categories: [],
          colors: [],
          attributes: [],
          priceRange: null,
        },
        sortBy: 'default',
        params: '',
      },
      requestDeduplicator: new RequestDeduplicator(),
      allFilteredProducts: [],
      categoryNameCache: null,
    };
  },
  computed: {
    searchPlaceholder() {
      if (this.isPackaging) {
        return "Zoek bijv. op 'dozen' of 'koffers'";
      }
      return "Zoek bijv. op 'goud'";
    },

    hasActiveFilters() {
      return (
        this.currentFilters.filters.categories.length > 0 ||
        this.currentFilters.filters.colors.length > 0 ||
        this.currentFilters.filters.attributes.length > 0 ||
        this.currentFilters.filters.priceRange !== null
      );
    },

    sortedProducts() {
      const hasActiveFiltersOrSearch =
        this.hasActiveFilters ||
        (this.localSearch && this.localSearch.trim() !== '');

      if (hasActiveFiltersOrSearch || this.allFilteredProducts.length === 0) {
        return this.allFilteredProducts;
      }

      const productCounts = new Map();
      const getCount = (productId) => {
        if (!productCounts.has(productId)) {
          productCounts.set(productId, this.quantityOf(productId));
        }
        return productCounts.get(productId);
      };

      const hasItemsInCart = this.allFilteredProducts.some(
        (p) => getCount(p.id) > 0,
      );

      if (!hasItemsInCart) {
        return this.allFilteredProducts;
      }

      return [...this.allFilteredProducts].sort((a, b) => {
        const countA = getCount(a.id);
        const countB = getCount(b.id);

        if (countA > 0 && countB === 0) return -1;
        if (countA === 0 && countB > 0) return 1;

        return 0;
      });
    },

    paginatedData() {
      return this.paginate(this.sortedProducts);
    },

    showPagination() {
      return this.paginatedData.totalPages > 1;
    },

    isMailboxSessionComputed() {
      const sessionMailbox =
        sessionStorage.getItem('s7_is_mailbox_session') === 'true';
      const dataMailbox = this.data?.isMailboxSession === true;
      const propMailbox = this.isMailboxSession === true;
      return sessionMailbox || dataMailbox || propMailbox;
    },

    // Calculate CTA position - always after a complete row (odd index for 2-column layout)
    mobileCTAIndex() {
      if (this.allProducts.length <= 6) return -1;
      const midpoint = Math.floor(this.allProducts.length / 2);
      // Ensure CTA appears after an odd index (after complete row of 2 items)
      return midpoint % 2 === 0 ? midpoint - 1 : midpoint;
    },
  },
  watch: {
    packageType: {
      handler(newPackageType, oldPackageType) {
        if (newPackageType !== oldPackageType) {
          this.initializeMailboxFilter();
          if (this.allProducts.length > 0) {
            const cachedProducts =
              window.globalProductCache[this.endpoint] || [];
            const filteredProducts =
              this.filterBrievenbusProducts(cachedProducts);
            this.allFilteredProducts = filteredProducts;
            this.resetPagination();
            this.allProducts = this.paginatedData.items;

            this.$nextTick(() => {
              if (this.$refs.productFilters) {
                this.$refs.productFilters.updateCategoryCounts();
                this.$refs.productFilters.updatePriceRanges();
              }
            });
          }
        }
      },
      immediate: false,
    },
    isMailboxSessionComputed: {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          if (this.allProducts.length > 0) {
            const cachedProducts =
              window.globalProductCache[this.endpoint] || [];
            const filteredProducts =
              this.filterBrievenbusProducts(cachedProducts);
            this.allFilteredProducts = filteredProducts;
            this.resetPagination();
            this.allProducts = this.paginatedData.items;

            this.$nextTick(() => {
              if (this.$refs.productFilters) {
                this.$refs.productFilters.updateCategoryCounts();
                this.$refs.productFilters.updatePriceRanges();
              }
            });
          }
        }
      },
      immediate: false,
    },
    'data.isMailboxSession': {
      handler(newValue, oldValue) {
        if (newValue !== oldValue) {
          if (this.allProducts.length > 0) {
            const cachedProducts =
              window.globalProductCache[this.endpoint] || [];
            const filteredProducts =
              this.filterBrievenbusProducts(cachedProducts);
            this.allFilteredProducts = filteredProducts;
            this.resetPagination();
            this.allProducts = this.paginatedData.items;

            this.$nextTick(() => {
              if (this.$refs.productFilters) {
                this.$refs.productFilters.updateCategoryCounts();
                this.$refs.productFilters.updatePriceRanges();
              }
            });
          }
        }
      },
      immediate: false,
    },
  },
  mounted() {
    if (
      !this.endpoint ||
      typeof this.endpoint !== 'string' ||
      this.endpoint.trim() === '' ||
      this.endpoint === 'false'
    ) {
      return;
    }

    const wasCacheCleared =
      sessionStorage.getItem('s7_cache_cleared') === 'true';

    if (wasCacheCleared) {
      delete window.globalProductCache[this.endpoint];
      sessionStorage.removeItem('s7_cache_cleared');
    }

    this.initializeMailboxFilter();
    this.fetchProducts();
    this.setupEventListeners();

    // Trigger reload if this is a mailbox session from cart editing
    if (this.isMailboxSessionComputed) {
      setTimeout(() => {
        document.dispatchEvent(
          new CustomEvent('s7_reload_products', {
            detail: { packageType: this.packageType },
          }),
        );
      }, 100);
    }
  },

  beforeUnmount() {
    this.removeEventListeners();
    this.clearDebouncedFunctions();
  },
  methods: {
    clearDebouncedFunctions() {
      // Clear debounced functions cache to prevent memory leaks
      if (this._debouncedFunctions) {
        this._debouncedFunctions.forEach((debouncedFn) => {
          if (debouncedFn.cancel) {
            debouncedFn.cancel();
          }
        });
        this._debouncedFunctions.clear();
      }

      // Clear request deduplicator
      if (this.requestDeduplicator) {
        this.requestDeduplicator.clear();
      }
    },

    handleFiltersChanged(filterData) {
      this.currentFilters = filterData;
      this.localSearch = filterData.search;
      this.resetPagination();
      this.categoryNameCache = null;

      if (this.fetchController) {
        this.fetchController.abort();
      }

      const cachedProducts = window.globalProductCache[this.endpoint] || [];
      let newProducts = this.filterBrievenbusProducts(cachedProducts);
      this.blurLoader = true;
      this.hasSearched = true;

      if (filterData.filters.categories.length > 0) {
        newProducts = newProducts.filter((product) => {
          const productCategoryIds = product.category_ids || [];
          const filterCategoryIds = filterData.filters.categories || [];

          return (
            filterCategoryIds.length === 0 ||
            filterCategoryIds.some((id) => productCategoryIds.includes(id))
          );
        });
      }

      if (filterData.filters.colors.length > 0) {
        newProducts = newProducts.filter((product) => {
          const productColorIds = product.color_term_ids || [];
          const filterColorIds = filterData.filters.colors || [];

          return (
            filterColorIds.length === 0 ||
            filterColorIds.some((id) => productColorIds.includes(id))
          );
        });
      }

      if (filterData.filters.attributes.length > 0) {
        newProducts = newProducts.filter((product) => {
          const productAttributeIds = product.attribute_term_ids || [];
          return filterData.filters.attributes.every((id) =>
            productAttributeIds.includes(id),
          );
        });
      }

      if (filterData.filters.priceRange) {
        const { min, max } = filterData.filters.priceRange;
        newProducts = newProducts.filter((product) => {
          const price = parseFloat(product.price);
          return price >= min && price <= max;
        });
      }

      if (this.localSearch && this.localSearch.trim() !== '') {
        const searchTerm = this.localSearch.trim().toLowerCase();
        const categoryCache = this.getCategoryNameCache();

        newProducts = newProducts.filter((product) => {
          const title = product.name ? product.name.toLowerCase() : '';
          const description = product.description
            ? product.description.toLowerCase()
            : '';
          const sku = product.sku ? product.sku.toLowerCase() : '';

          if (
            title.includes(searchTerm) ||
            description.includes(searchTerm) ||
            sku.includes(searchTerm)
          ) {
            return true;
          }

          if (product.category_ids && categoryCache) {
            return product.category_ids.some((catId) => {
              const categoryNames = categoryCache.get(catId);
              return (
                categoryNames &&
                categoryNames.some((name) =>
                  name.toLowerCase().includes(searchTerm),
                )
              );
            });
          }

          return false;
        });
      }

      // Apply sorting
      if (filterData.sortBy && filterData.sortBy !== 'default') {
        newProducts = this.sortProducts(newProducts, filterData.sortBy);
      }

      // Store alle gefilterde producten
      this.allFilteredProducts = newProducts;

      // Update products voor huidige pagina via computed property
      this.allProducts = this.paginatedData.items;

      this.blurLoader = false;

      this.$nextTick(() => {
        if (
          this.$refs.productFilters &&
          this.$refs.productFilters.$refs.searchInput &&
          !this.isMobileDevice()
        ) {
          this.$refs.productFilters.$refs.searchInput.focus();
        }
      });
    },

    fetchProducts() {
      const wasCacheCleared =
        sessionStorage.getItem('s7_cache_cleared') === 'true';

      if (window.globalProductCache[this.endpoint] && !wasCacheCleared) {
        const cachedProducts = window.globalProductCache[this.endpoint];

        const filteredProducts = this.filterBrievenbusProducts(cachedProducts);

        this.allFilteredProducts = filteredProducts;
        this.resetPagination();
        this.allProducts = this.paginatedData.items;
        this.isLoading = false;
        return;
      }

      // Use request deduplication to prevent multiple identical requests
      const requestKey = `fetchProducts_${this.endpoint}_${this.isMailboxSessionComputed}`;

      this.requestDeduplicator
        .deduplicate(requestKey, async () => {
          let url = `/api/products/${this.endpoint}`;
          const params = [];
          if (this.packageType === 'small') {
            params.push('mailbox=1');
          }
          params.push('_t=' + Date.now());
          if (params.length > 0) {
            url += '?' + params.join('&');
          }

          let response = await fetch(url, {
            headers: {
              'Cache-Control': 'no-cache, no-store, must-revalidate',
              Pragma: 'no-cache',
              Expires: '0',
            },
          });

          if (!response.ok) {
            if (response.status === 429) {
              await new Promise((resolve) => setTimeout(resolve, 3000));
              response = await fetch(url, {
                headers: {
                  'Cache-Control': 'no-cache, no-store, must-revalidate',
                  Pragma: 'no-cache',
                  Expires: '0',
                },
              });
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }
            } else {
              throw new Error(`HTTP error! status: ${response.status}`);
            }
          }

          const versionHeader = response.headers.get('X-Configurator-Version');
          if (versionHeader) {
            const versionResult = handleVersionChange(versionHeader);
            if (versionResult.changed) {
              window.globalProductCache = {};
              window.globalProductCacheTime = {};
            }
          }

          const data = await response.json();

          window.globalProductCache[this.endpoint] = data.items;
          window.globalProductCacheTime = window.globalProductCacheTime || {};
          window.globalProductCacheTime[this.endpoint] = Date.now();

          if (sessionStorage.getItem('s7_cache_cleared') === 'true') {
            sessionStorage.removeItem('s7_cache_cleared');
          }

          const filteredProducts = this.filterBrievenbusProducts(data.items);
          this.allFilteredProducts = filteredProducts;
          this.resetPagination();
          this.allProducts = this.paginatedData.items;

          this.isLoading = false;

          this.$nextTick(() => {
            if (this.$refs.productFilters) {
              this.$refs.productFilters.loadFilterOptions();
            }
          });
        })
        .catch((error) => {
          console.error('Error fetching products:', error);
          this.isLoading = false;
          // Show user-friendly error message
          this.$emit(
            'error',
            errorUtils.handleApiError(error, 'producten laden'),
          );
        });
    },

    quantityOf(id) {
      return getProductCount(this.data[this.dataKey], id);
    },

    selectProduct(product) {
      if (
        this.isMailboxSession &&
        !this.isProductBrievenbusCompatible(product)
      ) {
        alert(
          'Dit product is niet geschikt voor brievenbuspakketten. Kies een ander product.',
        );
        return;
      }

      if (this.isPackaging) {
        this.$emit('update', {
          packaging: product,
          [this.dataKey]: this.data[this.dataKey].map((p) => ({
            ...p,
            count: p.id === product.id ? 1 : 0,
          })),
        });
      } else {
        this.increase(product);
      }
    },
    increase(product) {
      // Validate mailbox compatibility in mailbox sessions
      if (
        this.isMailboxSession &&
        !this.isProductBrievenbusCompatible(product)
      ) {
        alert(
          'Dit product is niet geschikt voor brievenbuspakketten. Kies een ander product.',
        );
        return;
      }

      this.$emit('update', {
        [this.dataKey]: increaseProductCount(this.data[this.dataKey], product),
      });
    },
    decrease(product) {
      const existingIndex = this.data[this.dataKey].findIndex(
        (p) => p.id === product.id,
      );

      if (existingIndex >= 0) {
        if ((this.data[this.dataKey][existingIndex].count || 0) <= 1) {
          this.$emit('update', {
            ['personalization']: removePersonalizationUtil(
              this.data.personalization.productPersonalization,
              product.id,
            ),
          });
        }
      }

      this.$emit('update', {
        [this.dataKey]: decreaseProductCount(
          this.data[this.dataKey],
          product.id,
        ),
      });
    },
    remove(product) {
      this.$emit('update', {
        [this.dataKey]: removeProductUtil(this.data[this.dataKey], product.id),
      });
      this.$emit('update', {
        ['personalization']: removePersonalizationUtil(
          this.data.personalization.productPersonalization,
          product.id,
        ),
      });
    },
    goToPage(pageNumber) {
      // Use composable's pagination logic
      this.paginationGoToPage(pageNumber);

      // Update products after page change
      this.$nextTick(() => {
        this.allProducts = this.paginatedData.items;
      });
    },
    isSelected(product) {
      if (this.isPackaging && this.data.packaging) {
        return this.data.packaging.id === product.id;
      }
      return false;
    },
    // Open popup
    openProductDetailPopup(product) {
      this.productDetailModalProduct = product;
      this.showProductDetailModal = true;
    },
    debounce(func, wait = 300) {
      // Use improved debouncing with better performance
      if (!this._debouncedFunctions) {
        this._debouncedFunctions = new Map();
      }

      const key = `${func.name || 'anonymous'}_${wait}`;

      if (!this._debouncedFunctions.has(key)) {
        this._debouncedFunctions.set(
          key,
          debounce(func, wait, {
            leading: false,
            trailing: true,
            maxWait: wait * 2, // Prevent indefinite delays
          }),
        );
      }

      return this._debouncedFunctions.get(key)();
    },
    setupEventListeners() {
      // Verwijder eventuele bestaande listener om duplicatie te voorkomen
      if (this._reloadEventListener) {
        document.removeEventListener(
          's7_reload_products',
          this._reloadEventListener,
        );
      }

      this._reloadEventListener = (e) => {
        if (e.detail?.packageType) {
          // Emit packageType change to parent instead of mutating prop
          this.$emit('update', { packageType: e.detail.packageType });
          this.initializeMailboxFilter();

          if (this.fetchController) {
            this.fetchController.abort();
          }

          this.resetPagination();
          this.allProducts = [];
          this.isLoading = true;
          this.hasSearched = false;
          this.fetchProducts();
        }
      };

      document.addEventListener(
        's7_reload_products',
        this._reloadEventListener,
      );
    },

    removeEventListeners() {
      if (this._reloadEventListener) {
        document.removeEventListener(
          's7_reload_products',
          this._reloadEventListener,
        );
        this._reloadEventListener = null;
      }
    },

    filterBrievenbusProducts(products) {
      if (this.packageType !== 'small') {
        return products;
      }

      const filtered = products.filter((product) => {
        const attrs = product.attributes || [];

        const brievenbusAttr = attrs.find((attr) => {
          return (
            attr.name === 'pa_brievenbus-geschikt' ||
            attr.slug === 'pa_brievenbus-geschikt' ||
            attr.name?.toLowerCase().includes('brievenbus') ||
            attr.slug?.toLowerCase().includes('brievenbus') ||
            attr.name?.toLowerCase().includes('geschikt') ||
            attr.slug?.toLowerCase().includes('geschikt')
          );
        });

        if (!brievenbusAttr) {
          return false;
        }

        const isCompatible =
          brievenbusAttr.options?.includes?.('ja') ||
          brievenbusAttr.value === 'ja';

        return isCompatible;
      });

      return filtered;
    },

    initializeMailboxFilter() {
      // Also update packageType if needed - emit event to parent instead of mutating prop
      if (this.isMailboxSessionComputed && this.packageType !== 'small') {
        this.$emit('update', { packageType: 'small' });
      }
    },

    isProductBrievenbusCompatible(product) {
      if (!product) return false;

      const attrs = product.attributes || [];
      const brievenbusAttr = attrs.find((attr) => {
        return (
          attr.name === 'pa_brievenbus-geschikt' ||
          attr.slug === 'pa_brievenbus-geschikt' ||
          attr.name?.toLowerCase().includes('brievenbus') ||
          attr.slug?.toLowerCase().includes('brievenbus') ||
          attr.name?.toLowerCase().includes('geschikt') ||
          attr.slug?.toLowerCase().includes('geschikt')
        );
      });

      if (!brievenbusAttr) {
        return false;
      }

      return (
        brievenbusAttr.options?.includes?.('ja') ||
        brievenbusAttr.value === 'ja'
      );
    },
    debouncedFetchProducts() {
      this.debounce(() => {
        if (this.fetchController) {
          this.fetchController.abort();
        }

        this.page = 1;
        this.isLoading = true;
        this.allProducts = [];
        this.hasSearched = true;
        this.fetchProducts();
      }, 350);
    },
    clearSearch() {
      if (this.isMobileDevice()) {
        this.localSearch = '';
        this.hasSearched = false;
        this.$refs.productFilters?.clearSearchInput();
        this.$refs.productFilters?.handleSearchInput();
      } else {
        this.$refs.productFilters?.focusSearchInput();
      }
    },

    resetAllFilters() {
      this.hasSearched = false;
      if (this.$refs.productFilters) {
        this.$refs.productFilters.resetFiltersOnly();
      }
    },

    getActiveFiltersText() {
      const filters = [];

      if (this.currentFilters.filters.categories.length > 0) {
        const categoryNames = this.currentFilters.filters.categories.map(
          (catId) => {
            if (
              this.$refs.productFilters &&
              this.$refs.productFilters.filterOptions.categories
            ) {
              const category =
                this.$refs.productFilters.filterOptions.categories.find(
                  (cat) => cat.id === catId,
                );
              const name = category ? category.name : `Categorie ${catId}`;
              return this.decodeHtmlEntities(name);
            }
            return `Categorie ${catId}`;
          },
        );
        filters.push(categoryNames.join(', '));
      }

      if (this.currentFilters.filters.colors.length > 0) {
        const colorOptions =
          (this.$refs.productFilters &&
            this.$refs.productFilters.filterOptions.colors) ||
          [];
        const colorNames = this.currentFilters.filters.colors.map((colorId) => {
          const color = colorOptions.find((c) => c.term_id === colorId);
          return color ? color.name : `Kleur ${colorId}`;
        });
        filters.push(colorNames.join(', '));
      }

      if (this.currentFilters.filters.priceRange) {
        filters.push(this.currentFilters.filters.priceRange.label);
      }

      return filters.join(', ');
    },

    getActiveFilterCategoriesText() {
      const { categories, colors, priceRange } = this.currentFilters.filters;
      const names = [];
      if (Array.isArray(categories) && categories.length > 0) {
        const options =
          (this.$refs.productFilters &&
            this.$refs.productFilters.filterOptions.categories) ||
          [];
        const resolved = categories
          .map((id) => {
            const option = options.find((c) => c.id === id || c.term_id === id);
            const cachedName =
              this.$refs.productFilters?.categoryNamesCache?.[id];
            const name = option?.name || cachedName || `Categorie ${id}`;
            return this.decodeHtmlEntities(name);
          })
          .filter(Boolean);
        if (resolved.length) names.push(resolved.join(', '));
      }
      if (Array.isArray(colors) && colors.length > 0) {
        const colorOptions =
          (this.$refs.productFilters &&
            this.$refs.productFilters.filterOptions.colors) ||
          [];
        const colorNames = colors.map((colorId) => {
          const color = colorOptions.find((c) => c.term_id === colorId);
          return color ? color.name : `Kleur ${colorId}`;
        });
        names.push(colorNames.join(', '));
      }
      if (priceRange) {
        names.push(priceRange.label);
      }
      return names.join(', ');
    },

    isMobileDevice() {
      return deviceUtils.isMobile();
    },

    decodeHtmlEntities(text) {
      return htmlUtils.decodeHtml(text);
    },

    sortProducts(products, sortBy) {
      const sorted = [...products];

      switch (sortBy) {
        case 'name-asc':
          sorted.sort((a, b) =>
            (a.name || '').localeCompare(b.name || '', 'nl', {
              sensitivity: 'base',
            }),
          );
          break;
        case 'name-desc':
          sorted.sort((a, b) =>
            (b.name || '').localeCompare(a.name || '', 'nl', {
              sensitivity: 'base',
            }),
          );
          break;
        case 'price-asc':
          sorted.sort(
            (a, b) => parseFloat(a.price || 0) - parseFloat(b.price || 0),
          );
          break;
        case 'price-desc':
          sorted.sort(
            (a, b) => parseFloat(b.price || 0) - parseFloat(a.price || 0),
          );
          break;
        default:
          break;
      }

      return sorted;
    },

    getCategoryNameCache() {
      if (this.categoryNameCache) {
        return this.categoryNameCache;
      }

      const cache = new Map();
      let allCategories = [];

      if (this.$refs.productFilters?.filterOptions?.categories) {
        allCategories = this.$refs.productFilters.filterOptions.categories;
      } else if (window.globalFilterCache?.categories) {
        allCategories = window.globalFilterCache.categories;
      }

      allCategories.forEach((category) => {
        const catId = category.id || category.term_id;
        if (!cache.has(catId)) {
          cache.set(catId, []);
        }
        if (category.name) {
          cache.get(catId).push(category.name);
        }

        if (category.children && Array.isArray(category.children)) {
          category.children.forEach((child) => {
            const childId = child.id || child.term_id;
            if (!cache.has(childId)) {
              cache.set(childId, []);
            }
            if (child.name && !cache.get(childId).includes(child.name)) {
              cache.get(childId).push(child.name);
            }
            if (category.name && !cache.get(childId).includes(category.name)) {
              cache.get(childId).push(category.name);
            }
          });
        }
      });

      this.categoryNameCache = cache;
      return cache;
    },
  },
};
</script>
